import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Printer,
  Search,
  Send,
  ShoppingCart,
  Trash2,
  Truck,
  Wrench,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatMoney } from '@/lib/format'
import { useWorkOrders } from '@/features/service/hooks'
import {
  useChangePOStatus,
  useCreatePurchaseOrder,
  useDeletePurchaseOrder,
  usePurchaseOrder,
  usePurchaseOrders,
  useSendPurchaseOrder,
  useSuppliers,
  useUpdatePOLogistics,
  useWorkOrderParts,
} from '@/features/parts/hooks'
import { POETA, POTracking } from '@/features/parts/po-tracking'
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types/parts'

const statusVariant: Record<
  PurchaseOrderStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'outline',
  sent: 'default',
  received: 'secondary',
  canceled: 'destructive',
}

export function POStatusBadge({ status }: { status: PurchaseOrderStatus }) {
  return (
    <Badge variant={statusVariant[status]} className="capitalize">
      {status}
    </Badge>
  )
}

/** PurchaseOrdersPage lists all purchase orders. */
export function PurchaseOrdersList() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const { data: suppliers = [] } = useSuppliers()
  const {
    data: pos = [],
    isLoading,
    isError,
  } = usePurchaseOrders(
    statusFilter !== 'all' ? { status: statusFilter as PurchaseOrderStatus } : undefined,
  )

  // Client-side search across PO number and supplier.
  const needle = search.trim().toLowerCase()
  const visiblePOs = needle
    ? pos.filter(
        (po) =>
          po.po_number.toLowerCase().includes(needle) ||
          po.supplier_name.toLowerCase().includes(needle),
      )
    : pos

  return (
    <div className="flex flex-col gap-6">
      <Link to="/service">
        <Button variant="ghost" size="sm" className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Service
        </Button>
      </Link>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShoppingCart className="h-8 w-8" /> Purchase Orders
          </h1>
          <p className="text-muted-foreground">
            Draft POs from work order parts and send them to suppliers by email.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search PO or supplier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 sm:w-56"
            />
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Purchase Order
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="py-6 text-center text-sm text-destructive">
              Failed to load purchase orders.
            </p>
          ) : visiblePOs.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {needle
                ? 'No purchase orders match your search.'
                : 'No purchase orders yet — create one for a work order with the button above.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Work Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>ETA</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePOs.map((po) => (
                  <TableRow key={po.id}>
                    <TableCell>
                      <Link
                        to="/service/purchase-orders/$poId"
                        params={{ poId: po.id }}
                        className="font-mono text-xs font-medium hover:underline"
                      >
                        {po.po_number}
                      </Link>
                    </TableCell>
                    <TableCell>{po.supplier_name}</TableCell>
                    <TableCell>
                      {po.work_order_id && (
                        <Link
                          to="/service/$workOrderId"
                          params={{ workOrderId: po.work_order_id }}
                          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                        >
                          <Wrench className="h-3 w-3" /> View
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      <POStatusBadge status={po.status} />
                    </TableCell>
                    <TableCell className="text-xs">
                      <POETA po={po} />
                    </TableCell>
                    <TableCell>
                      <POTracking po={po} />
                    </TableCell>
                    <TableCell className="text-right">{po.lines.length}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatMoney(po.total)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(po.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {suppliers.find((s) => s.id === po.supplier_id)?.ordering_method ===
                      'distributor_api'
                        ? 'API supplier'
                        : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreatePODialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  )
}

/** PurchaseOrderDetail shows the printable PO with send + status actions. */
export function PurchaseOrderDetail({ poId }: { poId: string }) {
  const { data: po, isLoading, isError } = usePurchaseOrder(poId)
  const sendMutation = useSendPurchaseOrder()
  const statusMutation = useChangePOStatus()
  const deleteMutation = useDeletePurchaseOrder()

  const [sendOpen, setSendOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [shipTo, setShipTo] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (isError || !po) {
    return <p className="text-destructive">Purchase order not found.</p>
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="print:hidden">
        <Link to="/service/purchase-orders">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to Purchase Orders
          </Button>
        </Link>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between print:hidden">
        <div>
          <h1 className="break-words font-mono text-2xl font-bold tracking-tight">
            {po.po_number}
          </h1>
          <p className="text-muted-foreground">
            {po.supplier_name}
            {po.sent_via ? ` — sent via ${po.sent_via}` : ''}
          </p>
          {po.work_order_id && (
            <Link
              to="/service/$workOrderId"
              params={{ workOrderId: po.work_order_id }}
              className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              <Wrench className="h-3.5 w-3.5" /> From Work Order
            </Link>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
          {po.status === 'draft' && (
            <>
              <Button variant="outline" onClick={() => setSendOpen(true)}>
                <Send className="mr-2 h-4 w-4" /> Send to Supplier
              </Button>
              <Button
                variant="outline"
                className="text-destructive"
                onClick={() => {
                  setActionError(null)
                  setCancelOpen(true)
                }}
              >
                Cancel PO
              </Button>
            </>
          )}
          {po.status === 'sent' && (
            <Button
              onClick={() => {
                setActionError(null)
                statusMutation.mutate(
                  { id: po.id, status: 'received' },
                  { onError: (e) => setActionError(e instanceof Error ? e.message : 'Failed') },
                )
              }}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Mark Received
            </Button>
          )}
          {(po.status === 'draft' || po.status === 'canceled') && (
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              aria-label="Delete purchase order"
              onClick={() => {
                setActionError(null)
                setDeleteOpen(true)
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {actionError && <p className="text-sm text-destructive print:hidden">{actionError}</p>}

      {/* Printable PO document */}
      <Card className="print:border-0 print:shadow-none">
        <CardHeader>
          <CardTitle>Purchase Order {po.po_number}</CardTitle>
          <CardDescription>
            Supplier: {po.supplier_name} — {new Date(po.created_at).toLocaleDateString()}
            <POStatusBadge status={po.status} />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part number</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Unit</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {po.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-xs">{l.part_number || '—'}</TableCell>
                  <TableCell>{l.description}</TableCell>
                  <TableCell className="text-right">{l.quantity}</TableCell>
                  <TableCell className="text-right">{formatMoney(l.unit_cost)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(l.line_total)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4} className="text-right text-sm text-muted-foreground">
                  Total
                </TableCell>
                <TableCell className="text-right font-semibold">{formatMoney(po.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {po.notes && (
            <div className="mt-4">
              <p className="text-sm font-medium">Notes</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{po.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <DeliveryEditor key={`${po.id}:${po.updated_at}`} po={po} />

      {/* Cancel confirmation */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancel purchase order {po.po_number}?</DialogTitle>
            <DialogDescription>
              The PO is marked canceled and can no longer be sent to the supplier. Its part lines
              stay on the work order.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep Draft
            </Button>
            <Button
              variant="destructive"
              disabled={statusMutation.isPending}
              onClick={() => {
                setActionError(null)
                statusMutation.mutate(
                  { id: po.id, status: 'canceled' },
                  {
                    onSuccess: () => setCancelOpen(false),
                    onError: (e) => setActionError(e instanceof Error ? e.message : 'Failed'),
                  },
                )
              }}
            >
              {statusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancel PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete purchase order {po.po_number}?</DialogTitle>
            <DialogDescription>
              This cannot be undone. The PO and its line snapshots are removed; the work order's
              part lines are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                setActionError(null)
                deleteMutation.mutate(po.id, {
                  onSuccess: () => setDeleteOpen(false),
                  onError: (e) => setActionError(e instanceof Error ? e.message : 'Failed'),
                })
              }}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog
        open={sendOpen}
        onOpenChange={(o) => {
          setSendOpen(o)
          if (!o) setActionError(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send purchase order</DialogTitle>
            <DialogDescription>
              Emails the PO to the supplier's ordering address. When SMTP is not configured the
              message is logged instead (simulation mode).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5 py-1">
            <Label htmlFor="ship-to">Ship to</Label>
            <Input
              id="ship-to"
              value={shipTo}
              onChange={(e) => setShipTo(e.target.value)}
              placeholder="Marina name, address"
            />
          </div>
          {actionError && <p className="text-sm text-destructive">{actionError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>
              Back
            </Button>
            <Button
              disabled={sendMutation.isPending}
              onClick={() =>
                sendMutation.mutate(
                  { id: po.id, shipTo: shipTo || undefined },
                  {
                    onSuccess: () => setSendOpen(false),
                    onError: (e) =>
                      setActionError(e instanceof Error ? e.message : 'Failed to send PO'),
                  },
                )
              }
            >
              {sendMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Re-exported for potential reuse in work order views.
export type { PurchaseOrder }

/**
 * CreatePODialog builds a draft PO from scratch: pick the work order it
 * belongs to, the supplier, and which of the work order's part lines to
 * order (all lines preselected). The backend snapshots the selected lines, so
 * a work order without part lines can't produce a PO. When opened from a work
 * order page, presetWorkOrderId locks the choice to that order.
 */
export function CreatePODialog({
  open,
  onOpenChange,
  presetWorkOrderId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  presetWorkOrderId?: string
}) {
  const navigate = useNavigate()
  const createMutation = useCreatePurchaseOrder()
  const { data: suppliers = [] } = useSuppliers()
  const { data: workOrders = [] } = useWorkOrders()

  const [workOrderId, setWorkOrderId] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [notes, setNotes] = useState('')
  // null = every line on the work order; a list means the user narrowed it.
  const [pickedLineIds, setPickedLineIds] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const effectiveWorkOrderId = presetWorkOrderId ?? workOrderId
  const presetWorkOrder = presetWorkOrderId
    ? workOrders.find((wo) => wo.id === presetWorkOrderId)
    : undefined
  const { data: lines = [], isLoading: linesLoading } = useWorkOrderParts(effectiveWorkOrderId)
  const allLineIds = useMemo(() => lines.map((l) => l.id), [lines])
  const selectedLineIds = pickedLineIds ?? allLineIds

  const reset = () => {
    setWorkOrderId('')
    setSupplierId('')
    setNotes('')
    setPickedLineIds(null)
    setError(null)
  }

  const toggleLine = (id: string) => {
    const next = new Set(selectedLineIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setPickedLineIds([...next])
  }

  const handleCreate = () => {
    if (!effectiveWorkOrderId || !supplierId || selectedLineIds.length === 0) return
    setError(null)
    createMutation.mutate(
      {
        supplier_id: supplierId,
        work_order_id: effectiveWorkOrderId,
        line_ids: selectedLineIds,
        notes: notes || undefined,
      },
      {
        onSuccess: (po) => {
          onOpenChange(false)
          navigate({ to: '/service/purchase-orders/$poId', params: { poId: po.id } })
        },
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Failed to create purchase order'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Purchase Order</DialogTitle>
          <DialogDescription>
            Pick the work order this PO belongs to, choose the part lines to order, and select a
            supplier.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          <div className="grid gap-1.5">
            <Label>Work Order</Label>
            {presetWorkOrderId ? (
              <p className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
                {presetWorkOrder
                  ? `${presetWorkOrder.boat_name} — ${presetWorkOrder.title}`
                  : 'This work order'}
              </p>
            ) : (
              <Select
                value={workOrderId}
                onValueChange={(v) => {
                  setWorkOrderId(v)
                  setPickedLineIds(null)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a work order" />
                </SelectTrigger>
                <SelectContent>
                  {workOrders.map((wo) => (
                    <SelectItem key={wo.id} value={wo.id}>
                      {wo.boat_name} — {wo.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {effectiveWorkOrderId && (
            <div className="grid gap-1.5">
              <Label>Part lines</Label>
              {linesLoading ? (
                <div className="flex justify-center rounded-md border py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : lines.length === 0 ? (
                <p className="rounded-md border px-3 py-3 text-sm text-muted-foreground">
                  This work order has no part lines yet — add parts on the work order first.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto rounded-md border">
                  {lines.map((l) => (
                    <Label
                      key={l.id}
                      htmlFor={`po-line-${l.id}`}
                      className="flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm font-normal last:border-b-0 hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`po-line-${l.id}`}
                        checked={selectedLineIds.includes(l.id)}
                        onCheckedChange={() => toggleLine(l.id)}
                      />
                      <span className="font-mono text-xs">{l.part_number || '—'}</span>
                      <span className="flex-1 truncate">{l.description}</span>
                      <span className="text-xs text-muted-foreground">×{l.quantity}</span>
                      <span className="text-xs">{formatMoney(l.line_total)}</span>
                    </Label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-1.5">
            <Label>Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a supplier" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="new-po-notes">Notes</Label>
            <Input
              id="new-po-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — freight, core charges, …"
            />
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !workOrderId ||
              !supplierId ||
              selectedLineIds.length === 0 ||
              createMutation.isPending
            }
            onClick={handleCreate}
          >
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Purchase Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * DeliveryEditor edits the PO's ETA / carrier / tracking number. Keyed by the
 * PO's updated_at so a save (or any status change elsewhere) resets the form
 * from fresh server data. Cancelled POs keep their record read-only.
 */
function DeliveryEditor({ po }: { po: PurchaseOrder }) {
  const logisticsMutation = useUpdatePOLogistics()
  const [eta, setEta] = useState(po.expected_delivery ? po.expected_delivery.slice(0, 10) : '')
  const [carrier, setCarrier] = useState(po.carrier)
  const [tracking, setTracking] = useState(po.tracking_number)
  const [error, setError] = useState<string | null>(null)

  const readOnly = po.status === 'canceled'
  const dirty =
    eta !== (po.expected_delivery?.slice(0, 10) ?? '') ||
    carrier !== po.carrier ||
    tracking !== po.tracking_number

  const save = () => {
    setError(null)
    logisticsMutation.mutate(
      {
        id: po.id,
        body: {
          expected_delivery: eta ? new Date(`${eta}T12:00:00`).toISOString() : null,
          carrier: carrier.trim() || null,
          tracking_number: tracking.trim() || null,
        },
      },
      {
        onError: (e) => setError(e instanceof Error ? e.message : 'Failed to update delivery info'),
      },
    )
  }

  return (
    <Card className="print:hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="h-4 w-4" /> Delivery
          </CardTitle>
          <CardDescription>
            The supplier's promised date and shipment tracking — surfaced on the work order's Parts
            section.
          </CardDescription>
        </div>
        {!readOnly && (
          <Button size="sm" disabled={!dirty || logisticsMutation.isPending} onClick={save}>
            {logisticsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="grid gap-1.5">
            <Label htmlFor="po-eta">Expected delivery</Label>
            <Input
              id="po-eta"
              type="date"
              value={eta}
              disabled={readOnly}
              onChange={(e) => setEta(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="po-carrier">Carrier</Label>
            <Input
              id="po-carrier"
              value={carrier}
              disabled={readOnly}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="UPS, FedEx, freight…"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="po-tracking">Tracking number</Label>
            <Input
              id="po-tracking"
              value={tracking}
              disabled={readOnly}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="From the supplier's ship notice"
            />
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  )
}
